import { noop } from 'lodash';
import { createContext, useContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';

export interface AnnotatedPDFViewerContext {
  documentId: string | undefined;
  // Override the documentId for the current viewer. This is used to support multi-document summaries.
  setOverrideDocumentId: (documentId: string | null) => void;
  annotations: DocumentSearchResult[] | undefined;
  setAnnotations: (annotations: DocumentSearchResult[] | undefined) => void;
  activeAnnotationIndex: number;
  setActiveAnnotationIndex: (index: number) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  /** Move the active annotation index in the given direction. Pass e.g. -1 to go back or 1 to go forward. */
  moveActiveAnnotation: (direction: number) => void;
  overrideSearchQuery: string | undefined;
  onUpdateSearchQuery: () => void;
  searchLoading: boolean;
  setSearchLoading: (loading: boolean) => void;
  // Track the current citation request to prevent race conditions
  currentCitationRequestId: number;
  incrementCitationRequestId: () => number;
  showCitations: boolean;
  showCitationCoachmark: boolean;
  showCopyCitationsButton: boolean;
  lastCitationParagraphIdsRef: React.MutableRefObject<string[]>;
}

export const AnnotatedPDFViewerContext =
  createContext<AnnotatedPDFViewerContext>({
    documentId: undefined,
    setOverrideDocumentId: () => noop,
    annotations: undefined,
    setAnnotations: () => noop,
    activeAnnotationIndex: 0,
    setActiveAnnotationIndex: () => noop,
    searchQuery: '',
    setSearchQuery: () => noop,
    moveActiveAnnotation: () => noop,
    overrideSearchQuery: undefined,
    onUpdateSearchQuery: () => noop,
    searchLoading: false,
    setSearchLoading: () => noop,
    currentCitationRequestId: 0,
    incrementCitationRequestId: () => 0,
    showCitations: false,
    showCitationCoachmark: true,
    showCopyCitationsButton: false,
    lastCitationParagraphIdsRef: { current: [] },
  });

export const useAnnotatedPDFViewerContext = () => {
  return useGuardedContext(
    AnnotatedPDFViewerContext,
    'AnnotatedPDFViewerProvider'
  );
};

/**
 * Optional version of useAnnotatedPDFViewerContext that returns the default
 * values when used outside of an AnnotatedPDFViewerProvider.
 * Use this when you want to optionally use the context if available.
 */
export const useOptionalAnnotatedPDFViewerContext = () => {
  return useContext(AnnotatedPDFViewerContext);
};
